import React, { useState } from "react";
import {
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Divider,
} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useTranslation } from "react-i18next";
import {
  selectState,
  setInputText,
  setSelectedCase,
  convertCase,
  clearAll,
} from "./CaseConverterSlice";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import { ResultCard } from "../../components/result_card/ResultCard";
import { HomeCard } from "../../components/home_card/HomeCard";
import LoremIpsumGeneratorImage from "../../assets/LoremIpsumGenerator.webp";
import PasswordGeneratorImage from "../../assets/PasswordGenerator.webp";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function CaseConverter() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();
  const [resultCopied, setResultCopied] = useState(false);



  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setInputText(event.target.value));
  };

  const handleCaseChange = (event: any) => {
    dispatch(setSelectedCase(event.target.value));
  };

  const handleClear = () => {
    dispatch(clearAll());
    setResultCopied(false); // Reset copy state when clearing
  };

  const handleConvert = () => {
    dispatch(convertCase());
    setResultCopied(false); // Reset copy state when converting new text
  };

  const caseOptions = [
    { value: "sentence", label: t("caseConverter.sentenceCase") },
    { value: "lower", label: t("caseConverter.lowerCase") },
    { value: "upper", label: t("caseConverter.upperCase") },
    { value: "pascal", label: t("caseConverter.pascalCase") },
    { value: "camel", label: t("caseConverter.camelCase") },
    { value: "title", label: t("caseConverter.titleCase") },
    { value: "kebab", label: t("caseConverter.kebabCase") },
    { value: "snake", label: t("caseConverter.snakeCase") },
  ];

  return (
    <SideMenuPageContainer
      pageTitle={t("caseConverter.title")}
      pageSeoTitle={t("caseConverter.seoTitle")}
      pageDescription={t("caseConverter.description")}
    >
      <Grid2 container spacing={2} sx={{ mt: 2 }} columns={{ xs: 6, sm: 6, md: 12 }}>
        <Grid2 xs={12}>
          <Typography variant="h2" sx={{ mb: 2 }}>
            {t("caseConverter.mainHeading")}
          </Typography>
        </Grid2>

        <Grid2 xs={12} md={6}>
          <TextField
            fullWidth
            multiline
            rows={8}
            label={t("caseConverter.inputLabel")}
            value={state.inputText}
            onChange={handleInputChange}
            placeholder={t("caseConverter.inputPlaceholder")}
            sx={{ mb: 2 }}
          />
        </Grid2>

        <Grid2 xs={12} md={6}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t("caseConverter.caseTypeLabel")}</InputLabel>
            <Select
              value={state.selectedCase}
              label={t("caseConverter.caseTypeLabel")}
              onChange={handleCaseChange}
            >
              {caseOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleConvert}
            sx={{ mb: 2 }}
            fullWidth
          >
            {t("caseConverter.convertButton")}
          </Button>
          <Button
            variant="outlined"
            onClick={handleClear}
            sx={{ mb: 2 }}
            fullWidth
          >
            {t("caseConverter.clearButton")}
          </Button>
        </Grid2>

                 {state.outputText && (
           <Grid2 xs={12}>
             <ResultCard
               value={state.outputText}
               symbol=""
               description={t("caseConverter.resultTitle")}
               resultCopied={resultCopied}
               onCopy={(text, result) => {
                 setResultCopied(result);
                 // Reset copy state after 2 seconds
                 setTimeout(() => {
                   setResultCopied(false);
                 }, 2000);
               }}
             />
           </Grid2>
         )}

        <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
          <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
        </Grid2>

        {/* SEO Content Section */}
        <Grid2 xs={12}>
          <AdSense
              adSlot={ADSENSE_SLOTS.IN_ARTICLE}
              adFormat="fluid"
              adLayoutKey="-fb+5w+4e-db+86"
              sx={{ mb: 3 }}
            />
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("caseConverter.content.freeHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("caseConverter.content.freeDescription")}
          </Typography>

          <Typography variant="h4" sx={{ mb: 2 }}>
            {t("caseConverter.content.whatIsHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("caseConverter.content.whatIsDescription")}
          </Typography>

          <Typography variant="h4" sx={{ mb: 2 }}>
            {t("caseConverter.content.caseTypesHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("caseConverter.content.caseTypesDescription")}
          </Typography>

          <Typography variant="h4" sx={{ mb: 2 }}>
            {t("caseConverter.content.howToHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("caseConverter.content.howToDescription")}
          </Typography>
        </Grid2>

        {/* Related Tools Section */}
        <Grid2 xs={12}>
          <Typography variant="h3" sx={{ mt: 3, mb: 1 }}>
            {t("caseConverter.related")}
          </Typography>
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="loremIpsum"
            link="/LoremIpsumGenerator"
            smallCard={true}
            image={LoremIpsumGeneratorImage}
          />
        </Grid2>
        <Grid2 xs={6}>
          <HomeCard
            languageResource="passwordGenerator"
            link="/PasswordGenerator"
            smallCard={true}
            image={PasswordGeneratorImage}
          />
        </Grid2>
      </Grid2>
    </SideMenuPageContainer>
  );
}
